import React, { useEffect, useRef } from 'react';

/**
 * A row of photographs sitting in perspective space. As the section scrolls
 * past, the row tracks sideways and each card rotates toward the viewer —
 * so the set reads as depth rather than a flat filmstrip.
 */
const PhotoCorridor = ({ images }) => {
  const sectionRef = useRef(null);
  const trackRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    const track = trackRef.current;
    if (!section || !track) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;

    let raf = 0;
    let visible = false;

    const update = () => {
      const rect = section.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      // 0 → 1 across the pinned scroll distance
      const p = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;

      const cards = track.children;
      const maxShift = track.scrollWidth - window.innerWidth + 96;
      track.style.transform = `translate3d(${-p * Math.max(0, maxShift)}px, 0, 0)`;

      for (let i = 0; i < cards.length; i += 1) {
        const card = cards[i];
        const cardRect = card.getBoundingClientRect();
        const center = cardRect.left + cardRect.width / 2;
        const offset = (center - window.innerWidth / 2) / window.innerWidth;
        card.style.transform =
          `rotateY(${offset * -22}deg) translateZ(${-Math.abs(offset) * 140}px)`;
        card.style.opacity = String(Math.max(0.35, 1 - Math.abs(offset) * 0.85));
      }
      if (visible) raf = requestAnimationFrame(update);
    };

    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) raf = requestAnimationFrame(update);
      else cancelAnimationFrame(raf);
    }, { threshold: 0 });

    io.observe(section);
    update();

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, [images]);

  return (
    <section className="corridor" ref={sectionRef}>
      <div className="corridor-sticky">
        <div className="corridor-heading edge">
          <div className="wrap">
            <span className="label">Gallery</span>
            <h2 className="display-3" style={{ marginTop: '0.6rem' }}>A look around</h2>
          </div>
        </div>
        <div className="corridor-stage">
          <div className="corridor-track" ref={trackRef}>
            {images.map((img, i) => (
              <figure className="corridor-card" key={img.slug}>
                {img.webp ? (
                  <picture>
                    <source
                      srcSet={`${img.webpSmall} 640w, ${img.webp} 1200w`}
                      sizes="(max-width: 700px) 80vw, 42vw"
                      type="image/webp"
                    />
                    <img src={img.src} alt={img.caption} loading={i < 2 ? 'eager' : 'lazy'} />
                  </picture>
                ) : (
                  <img src={img.src} alt={img.caption} loading={i < 2 ? 'eager' : 'lazy'} />
                )}
                <figcaption>
                  <span className="corridor-index">{String(i + 1).padStart(2, '0')}</span>
                  {img.caption}
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default PhotoCorridor;
